import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { pipe } from 'fp-ts/function';
import { DescriptionItem, Loader } from '@mews-commander/ui';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ApplicationState } from '@mews-commander/types';
import { getServicePerTimeUnitLocalization } from '@mews-commander/legacy-entities/src/utils/serviceTimeUnitLocalization';
import { isCurrentEnterpriseInMultiTaxEnvironment } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import CommonReservationsDefaultProductOrderAverateAmount from './CommonReservationsDefaultProductOrderAverateAmount';
import ReservationsProductOrderOrderItemsAverageAmount from './ReservationsProductOrderOrderItemsAverageAmount';
import ReservationsTotalAmount from './ReservationsTotalAmount';

interface OwnProps {
    serviceId: string;
    reservationId: string;
    scope: string;
    isFetching: boolean;
}
interface StateProps {
    isMultiTaxEnvironment: boolean;
    perTimeUnitLocalized: string;
}
type Props = StateProps & OwnProps;

const ReservationPricing = ({
    serviceId,
    reservationId,
    isFetching,
    isMultiTaxEnvironment,
    perTimeUnitLocalized,
}: Props) => {
    if (isFetching) {
        return <Loader />;
    }

    return (
        <>
            <DescriptionItem
                definitionHighlighted
                data-test-id="averageTimeUnitRateShort"
                term={
                    <LocalizedText
                        textKey={TextKey.AverageTimeUnitRateShort}
                        substitutes={{ timeUnit: perTimeUnitLocalized }}
                    />
                }
                definition={
                    <CommonReservationsDefaultProductOrderAverateAmount
                        serviceId={serviceId}
                        serviceOrderIds={[reservationId]}
                        net={isMultiTaxEnvironment}
                    />
                }
            />
            <DescriptionItem
                definitionHighlighted
                data-test-id="averageTimeUnitPriceWithProducts"
                term={
                    <LocalizedText
                        textKey={TextKey.AverageTimeUnitPriceWithProducts}
                        substitutes={{ timeUnit: perTimeUnitLocalized }}
                    />
                }
                definition={
                    <ReservationsProductOrderOrderItemsAverageAmount
                        serviceId={serviceId}
                        serviceOrderIds={[reservationId]}
                        net={isMultiTaxEnvironment}
                    />
                }
            />
            <DescriptionItem
                definitionHighlighted
                data-test-id="totalCost"
                term={<LocalizedText textKey={TextKey.TotalCost} />}
                definition={<ReservationsTotalAmount serviceOrderIds={[reservationId]} net={isMultiTaxEnvironment} />}
            />
            {isMultiTaxEnvironment ? (
                <DescriptionItem
                    definitionHighlighted
                    data-test-id="totalCostGross"
                    term={<LocalizedText textKey={TextKey.TotalCostGross} />}
                    definition={<ReservationsTotalAmount serviceOrderIds={[reservationId]} net={false} />}
                />
            ) : null}
        </>
    );
};

export default pipe(
    ReservationPricing,
    connect<StateProps, {}, OwnProps, ApplicationState>(
        createStructuredSelector({
            perTimeUnitLocalized: (state: ApplicationState, { serviceId }: OwnProps) =>
                getServicePerTimeUnitLocalization(state, { id: serviceId }),
            isMultiTaxEnvironment: (state) => isCurrentEnterpriseInMultiTaxEnvironment(state),
        })
    )
);
